import { useEffect, useRef, useState } from "react";

// RubinOT custom outfits come from the `/api/outfit-proxy` endpoint as a
// horizontal sprite strip (one row of frames). Each source frame is 128px wide.
// We measure the loaded image to derive the frame count, then animate the strip
// with a CSS steps() animation so the outfit moves like the native GIF outfits.
const SOURCE_FRAME_WIDTH = 128;

export const isSpriteSheet = (src?: string | null): boolean =>
  !!src && /outfit-proxy/i.test(src);

interface SpriteSheetProps {
  src: string;
  disabled?: boolean;
  className?: string;
}

export const SpriteSheet = ({ src, disabled, className }: SpriteSheetProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [frameCount, setFrameCount] = useState<number | null>(null);
  const [size, setSize] = useState(0);

  // Load the strip to compute how many frames it contains.
  useEffect(() => {
    let cancelled = false;
    setFrameCount(null);
    const img = new Image();
    const done = () => {
      if (cancelled) return;
      setFrameCount(
        img.naturalWidth > 0
          ? Math.max(1, Math.round(img.naturalWidth / SOURCE_FRAME_WIDTH))
          : 1,
      );
    };
    img.onload = done;
    img.onerror = done;
    img.src = src;
    if (img.complete && img.naturalWidth > 0) done();
    return () => {
      cancelled = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [src]);

  // Track the container size so each frame fills its slot.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setSize(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const ready = frameCount !== null && size > 0;

  const style = ready
    ? ({
        "--sprite-frame-w": `${size}px`,
        "--sprite-frame-h": `${size}px`,
        "--sprite-strip-w": `${size * (frameCount as number)}px`,
        "--sprite-url": `url("${src.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}")`,
        "--sprite-duration": `${0.1 * (frameCount as number)}s`,
        "--sprite-frame-count": frameCount as number,
      } as React.CSSProperties)
    : undefined;

  return (
    <div
      ref={ref}
      className={`w-full h-full flex items-center justify-center ${
        disabled ? "grayscale opacity-50" : ""
      } ${className ?? ""}`}
    >
      {ready && <div className="sprite-sheet" style={style} role="img" />}
    </div>
  );
};

export default SpriteSheet;
